/**
 * SF-4762 — display helpers for the Platform feature flags page.
 * Kept out of the component files (react-refresh/only-export-components).
 */
import type { PlatformFlag, PlatformFlagChange } from './api/platformFlagsClient';

/** The words the email list editor uses for one flag. */
export interface ListWording {
  heading: string;
  /** One line under the heading, or null for none. */
  help: string | null;
  empty: string;
  inputLabel: string;
  saveText: string;
  savedText: string;
  /** Prefix of a list change in the flag's history. */
  historyPrefix: string;
}

/** Today's exempt-list wording (SF-4762): used whenever a flag has no listLabel. */
export const EXEMPT_LIST_WORDING: ListWording = {
  heading: 'Exempt addresses',
  help: 'These addresses can still sign in with a password.',
  empty: 'No exempt addresses.',
  inputLabel: 'Exempt email address',
  saveText: 'Save exempt list',
  savedText: 'Exempt list saved',
  historyPrefix: 'Exempt list',
};

/**
 * SF-4801 — a flag with its own listLabel (and optional listHelp) shows that
 * wording; any other flag keeps the exempt-list wording exactly.
 */
export function listWording(flag: Pick<PlatformFlag, 'listLabel' | 'listHelp'>): ListWording {
  const label = typeof flag.listLabel === 'string' ? flag.listLabel.trim() : '';
  if (!label) return EXEMPT_LIST_WORDING;
  const help = typeof flag.listHelp === 'string' ? flag.listHelp.trim() : '';
  return {
    heading: label,
    help: help || null,
    empty: 'No addresses yet.',
    inputLabel: `Email address to add to ${label.toLowerCase()}`,
    saveText: 'Save list',
    savedText: `${label} saved`,
    historyPrefix: label,
  };
}

/** "16 Oct 2026, 14:05" in the viewer's locale; em dash when missing or bad. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '\u2014';
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

const asList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** One plain-English line per history row. */
export function describeChange(
  change: PlatformFlagChange,
  listName: string = EXEMPT_LIST_WORDING.historyPrefix,
): string {
  if (change.field === 'enabled') {
    return change.new === true ? 'Switched on' : 'Switched off';
  }
  const before = asList(change.old);
  const after = asList(change.new);
  const added = after.filter((e) => !before.includes(e));
  const removed = before.filter((e) => !after.includes(e));
  const parts = [
    added.length ? `added ${added.join(', ')}` : '',
    removed.length ? `removed ${removed.join(', ')}` : '',
  ].filter(Boolean);
  return `${listName}: ${parts.join('; ') || 'no change'}`;
}
